import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import CountryFlag from '@dealroadshow/uikit/core/components/CountryFlag';
import { TabLink as NextNavLink } from '@/Framework/Router/Next/Link';

import styles from './overview.scss';

const anchors = [
  {
    anchor: 'why-research-regulated',
    title: 'Why is pre-IPO research distribution regulated?',
  },
  {
    anchor: 'regulations-apply',
    title: 'What regulations apply to pre-IPO research?',
  },
  {
    anchor: 'basic-requirements',
    title: 'What are the basic requirements?',
  },
  {
    anchor: 'regulations-impact',
    title: 'How do these regulations impact the timing of publication?',
  },
  {
    anchor: 'transactions',
    title: 'What types of transactions are covered?',
  },
  {
    anchor: 'additional-resources',
    title: 'Additional Resources',
  },
];

const Overview = () => {
  const [currentAnchor, setCurrentAnchor] = useState(anchors[0].anchor);
  const [isAnchorClicked, setAnchorClicked] = useState(false);
  const sectionRefs = useRef({});

  useEffect(() => {
    anchors.forEach((heading) => {
      sectionRefs.current[heading.anchor] = document.getElementById(heading.anchor);
    });

    const observer = new IntersectionObserver(
      (entries) => {
        const activeTargetId = entries.find((entry) => entry.isIntersecting)?.target.id;
        if (activeTargetId && !isAnchorClicked) {
          setCurrentAnchor(activeTargetId);
        }
      },
      {
        threshold: 0.5,
        rootMargin: '0px 0px -50% 0px',
      },
    );

    anchors.forEach((heading) => {
      if (sectionRefs.current[heading.anchor]) {
        observer.observe(sectionRefs.current[heading.anchor]);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [sectionRefs]);

  const anchorHandler = (anchor: string) => {
    setAnchorClicked(true);
    const section = sectionRefs.current[anchor];
    if (section) {
      setCurrentAnchor(anchor);
      section.scrollIntoView({ behavior: 'smooth' })
        .then(() => {
          setTimeout(() => setAnchorClicked(false), 500);
        });
    }
  };

  return (
    <>
      <div className={ styles.overviewContentContainer }>
        <div className={ styles.overviewContent }>
          <div className={ styles.sectionsListWrp }>
            <ul>
              { anchors.map(({
                anchor,
                title,
              }) => (
                <li key={ anchor } className={ cn({ [styles.activeLink]: currentAnchor === anchor }) }>
                  <NextNavLink to={ `#${ anchor }` } onClick={ anchorHandler }>
                    { title }
                  </NextNavLink>
                </li>
              )) }
            </ul>
          </div>
          <div>
            <div id="why-research-regulated" className={ styles.item }>
              <h2>Why is pre-IPO research distribution regulated?</h2>
              <p>
                In order to improve the quality and timeliness of key information available
                to investors in the days leading up to an equity initial public offering ("IPO"),
                a series of regulatory standards were released between 2008-2017 across EMEA and APAC regions,
                among others.
              </p>
              <p>
                These regulations set requirements and provide guidance for the production
                and dissemination of pre-IPO research in order to ensure market integrity is
                not compromised and consumer protections remain effective.
              </p>
              <p>
                Regulations will vary across countries and regions, but we’ve included
                an overview of regulations in leading IPO markets.
              </p>
              <p>
                To download original documentation, see Additional Resources below.
              </p>
            </div>
            <div id="regulations-apply" className={ styles.item }>
              <h2>
                What regulations apply to pre-IPO research?
              </h2>
              <p>
                There are considerable similarities in the rules surrounding pre-IPO
                research across APAC and EMEA. These regulations, by region, include:
              </p>
              <ul>
                <li>
                  <b><CountryFlag countryCode="gb" className={ styles.flagIcon } /> United Kingdom (2017)</b>
                  <br />
                  Financial Conduct Authority (FCA) Code of Business Sourcebook (COBS) Chapter 11A & Chapter 12.2
                </li>
                <li>
                  <b><CountryFlag countryCode="eu" className={ styles.flagIcon } /> European Union (2008-2017)</b>
                  <br />
                  Delegated Regulation 2017/565 Article 36(1) ("MiFID II Org Regulation")
                </li>
                <li>
                  <b><CountryFlag countryCode="hk" className={ styles.flagIcon } /> Hong Kong (2013)</b>
                  <br />
                  Securities and Futures Commission (SFC) Code of Conduct & Corporate
                  Finance Adviser Code of Conduct ("CFA Code")
                </li>
                <li>
                  <b><CountryFlag countryCode="sg" className={ styles.flagIcon } /> Singapore (2012)</b>
                  <br />
                  The Monetary Authority of Singapore (MAS) Securities and Futures Act Section 251(9)
                </li>
              </ul>
            </div>
            <div id="basic-requirements" className={ cn(styles.item, styles.requirementsContainer) }>
              <h2>
                What are the basic requirements of pre-IPO research regulations?
              </h2>
              <p>
                COBS 11A (United Kingdom) rules are some of the strictest pre-IPO
                research distribution requirements across regions. At a high level, they cover:
              </p>
              <ol>
                <li>
                  <b>Fair presentation:</b> Research must present a fair and balanced view without omitting
                  material facts or misleading information about the issuer and securities.
                </li>
                <li>
                  <b>Disclosure of interests:</b> Any financial interests or conflicts of interest related to
                  the issuer that could impair the impartiality of the research must be
                  clearly disclosed.
                </li>
                <li>
                  <b>Research identification:</b> The research document must be clearly identified as investment
                  research, and any substantive research recommendations must be
                  presented fairly and clearly
                  distinguished from other content.
                </li>
                <li>
                  <b>Distribution restrictions:</b> Pre-IPO research can only be distributed to
                  certain categories of recipients, such as professional clients,
                  eligible
                  counterparties, or investors meeting certain criteria related to
                  knowledge,
                  experience, and financial situation.
                </li>
                <li>
                  <b>Record keeping:</b> Firms must maintain records of the recipients of pre-IPO research
                  and the steps taken to ensure compliance with distribution restrictions.
                </li>
                <li>
                  <b>Internal systems and controls:</b>
                  <ol type="a">
                    <li>
                      Implement policies, procedures, systems and controls to ensure FCA research rule compliance.
                    </li>
                    <li>
                      Physical separation of research and investment banking/other functions that could create conflicts
                      ("not presented under the same cover").
                    </li>
                    <li>Compensation policies that eliminate improper influences on analysts' views.
                    </li>
                    <li>Supervision, review, and disciplinary processes for analysts.</li>
                  </ol>
                </li>
              </ol>
            </div>
            <div id="regulations-impact" className={ styles.item }>
              <h2>
                How do these regulations impact the timing of IPO research publication?
              </h2>
              <p>
                <b>In the United Kingdom and the European Union,</b> there is no mandatory blackout period.
              </p>
              <p>
                The FCA opted to exclude a blackout period from regulations when the majority of buy-side
                firms expressed opposition to an "unnecessary lengthening of the IPO timetable". However,
                the FCA requires that if unconnected analysts get equal access to management, connected research
                can be published 1 day after the prospectus. Otherwise, a 7-day minimum gap is expected.
              </p>
              <p>
                <b>In Singapore and Hong Kong,</b> regulations require a 14-day blackout period before the IPO prices.
              </p>
              <p>
                This is to ensure that investors have sufficient time to review and analyze the final prospectus
                without being influenced by research reports.
              </p>
              <p>
                After the 14 day period, research coverage can resume with analysts publishing their initiation reports
                on the newly listed company.
              </p>
            </div>
            <div id="transactions" className={ styles.item }>
              <h2>What types of transactions are covered?</h2>
              <p>
                <b>In the United Kingdom,</b> COBS 11A rules apply to all IPOs where shares are traded
                on a regulated market in the UK.
              </p>
              <p>
                COBS 11A rules do not apply to IPOs on the Alternative Investment Market (AIM)
                or other multilateral trading facilities (MTFs). However, the FCA encourages banks
                working on MTF IPOs to consider following them.
              </p>
              <p>
                <b>In the European Union,</b> MiFID regulations apply to equities, commodities, debt instruments,
                futures and options, exchange-traded funds, and currencies.
              </p>
              <p>
                <b>In Hong Kong,</b> the CFA Code applies to activities where a Corporate Finance Adviser is part
                of a professional firm or group of companies undertaking other activities, e.g.
                auditing, banking,
                research, stock broking and fund management.
              </p>
              <p>
                <b>In Singapore,</b> Securities
                and Futures Act Section 251(9) applies to all securities and securities-based
                derivative contracts.
              </p>
            </div>
            <div id="additional-resources" className={ styles.item }>
              <h2>
                Additional Resources
              </h2>
              <p>
                <b><CountryFlag countryCode="eu" className={ styles.flagIcon } /> European Union</b>
                <br />
                <a
                  href="https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A32017R0565"
                  target="_blank"
                >
                  EUR-Lex’s Commission Delegated Regulation
                </a>
              </p>
              <p>
                <b><CountryFlag countryCode="gb" className={ styles.flagIcon } /> United Kingdom</b>
                <br />
                <a
                  href="https://www.fca.org.uk/publication/policy/ps17-23.pdf"
                  target="_blank"
                >
                  FCA’s policy statement & final rules for COBS 11A
                </a>
                <br />
                <a
                  href="https://www.fca.org.uk/publication/discussion/dp16-3.pdf"
                  target="_blank"
                >
                  FCA’s discussion paper regarding the availability of information in the UK IPO process
                </a>
                <br />
                <a
                  href="https://www.afme.eu/Portals/0/globalassets/downloads/industry-guidelines/AFME-ECM-IPO-Reform-Q%26A-COBS-11A.pdf"
                  target="_blank"
                >
                  AFME’s UK IPO reform Q&A in relation to COBS 11A
                </a>
                <br />
                <a
                  href="https://www.handbook.fca.org.uk/handbook/COBS/11A/?view=chapter"
                  target="_blank"
                >
                  FCA’s handbook for COBS 11A rules
                </a>
              </p>
              <p>
                <b> <CountryFlag countryCode="hk" className={ styles.flagIcon } />Hong Kong</b>
                <br />
                <a
                  href="https://apps.sfc.hk/edistributionWeb/api/consultation/openFile?lang=EN&refNo=10CP5"
                  target="_blank"
                >
                  SFC’s consultation paper on the regulatory framework for
                  pre-deal research
                </a>
                <br />
                <a
                  href="https://www.asifma.org/wp-content/uploads/2018/07/asifma-pre-deal-research-addenda-for-the-hong-kong-ipo-process.pdf"
                  target="_blank"
                >
                  ASIFMA’s pre-deal research addenda for the Hong Kong IPO process
                </a>
              </p>
              <p>
                <b><CountryFlag countryCode="sg" className={ styles.flagIcon } /> Singapore</b>
                <br />
                <a
                  href="https://www.mas.gov.sg/-/media/MAS/resource/legislation_guidelines/securities_futures/sub_legislation/Securities_and_Futures_Bill_2001.pdf?la=en&hash=2380F489AA460CC9A7822819CDD4B6A1B8F96645"
                  target="_blank"
                >
                  MAS’ Securities and Futures Act
                </a>
                <br />
                <a
                  href="https://www.mas.gov.sg/-/media/mas-media-library/publications/monographs-or-information-paper/2023/cf-thematic-insp-info-paper.pdf"
                  target="_blank"
                >
                  MAS’ good practices and key findings information paper for IPOs
                </a>
              </p>
              <p className={ styles.bottomNote }>
                Please note that this article is provided by Finsight Group Inc.
                for educational and informational purposes only and is not intended to be construed as legal advice.
                Please seek the advice of counsel where appropriate.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className={ styles.divider } />
    </>
  );
};

export default Overview;
